import styled, { keyframes } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(16px) scale(0.98);
	}

	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
`;

const floatOrb = keyframes`
	0%, 100% { transform: translate(0, 0); }
	50% { transform: translate(10px, -10px); }
`;

const shimmer = keyframes`
	0% { background-position: 200% center; }
	100% { background-position: -200% center; }
`;

export const AgeGateOverlay = styled.div`
	position: fixed;
	inset: 0;
	z-index: 9000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	background: rgba(3, 1, 5, 0.58);
	backdrop-filter: blur(24px) saturate(1.08);
	overflow: hidden;

	&::before,
	&::after {
		content: '';
		position: absolute;
		border-radius: 50%;
		filter: blur(90px);
		pointer-events: none;
		animation: ${floatOrb} 9s ease-in-out infinite;
	}

	&::before {
		width: 460px;
		height: 460px;
		top: -140px;
		left: -100px;
		background: rgba(176, 38, 255, 0.26);
	}

	&::after {
		width: 380px;
		height: 380px;
		right: -80px;
		bottom: -120px;
		background: rgba(255, 77, 242, 0.18);
		animation-delay: -4.5s;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 0;
		align-items: flex-start;
		justify-content: flex-start;
	}
`;

export const AgeGateCard = styled.article`
	position: relative;
	z-index: 1;
	width: min(440px, calc(100vw - 40px));
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: 18px;
	background:
		radial-gradient(circle at 74% 0%, rgba(255, 77, 242, 0.13), transparent 34%),
		rgba(18, 7, 20, 0.96);
	box-shadow:
		0 34px 110px rgba(0, 0, 0, 0.72),
		0 0 36px rgba(255, 77, 242, 0.12);
	animation: ${fadeIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 100%;
		min-height: 100dvh;
		border-radius: 0;
		border: 0;
	}
`;

export const AgeGateContent = styled.section`
	position: relative;
	display: flex;
	flex-direction: column;
	min-width: 0;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 100dvh;
		flex: 1;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;
	}
`;

export const AgeGateBrand = styled.div`
	display: flex;
	align-items: center;
	padding: 22px 26px 0;

	img {
		width: 112px;
		height: auto;
		display: block;
		filter: drop-shadow(0 0 18px rgba(255, 77, 242, 0.22));
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 18px 20px 0;
	}
`;

export const AgeGateBody = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 28px 26px 24px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 18px 20px calc(18px + env(safe-area-inset-bottom, 0px));
	}
`;

export const AgeGateAccent = styled.div`
	width: 72px;
	height: 3px;
	margin-bottom: 18px;
	border-radius: 999px;
	background: linear-gradient(
		90deg,
		${THEME.violet},
		${THEME.pink} 50%,
		${THEME.gold}
	);
	background-size: 200% auto;
	animation: ${shimmer} 3.2s linear infinite;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		margin-bottom: 14px;
	}
`;

export const AgeGateTitle = styled.h1`
	margin: 0 0 10px;
	font-family: ${THEME.fontDisplay};
	font-size: 1.45rem;
	font-weight: 800;
	line-height: 1.15;
	color: ${THEME.text};
`;

export const AgeGateMessage = styled.p`
	margin: 0 0 28px;
	font-family: ${THEME.fontBody};
	font-size: 0.9rem;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.72);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		margin-bottom: 18px;
	}
`;

export const AgeGateActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding-top: 0;
	}
`;

export const AgeGateLegal = styled.p`
	margin: 14px 0 0;
	font-family: ${THEME.fontBody};
	font-size: 0.72rem;
	line-height: 1.45;
	text-align: center;
	color: rgba(255, 255, 255, 0.48);

	a {
		color: ${THEME.pink};
		font-weight: 700;
		text-decoration: none;

		&:hover {
			color: ${THEME.text};
			text-decoration: underline;
			text-underline-offset: 2px;
		}
	}
`;

export const AgeGateConfirmButton = styled.button`
	width: 100%;
	height: 46px;
	border: 0;
	border-radius: 999px;
	padding: 0 20px;
	font-family: ${THEME.fontApp};
	font-size: 0.9rem;
	font-weight: 800;
	color: #fff;
	background: ${THEME.gradientPrimary};
	box-shadow: 0 10px 28px rgba(176, 38, 255, 0.28);
	cursor: pointer;
	transition:
		transform 160ms ease,
		box-shadow 160ms ease;

	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 14px 32px rgba(255, 77, 242, 0.34);
	}

	&:active {
		transform: translateY(0);
	}
`;

export const AgeGateDeclineButton = styled.button`
	width: 100%;
	height: 42px;
	border: 0;
	border-radius: 999px;
	padding: 0 20px;
	font-family: ${THEME.fontApp};
	font-size: 0.84rem;
	font-weight: 700;
	color: rgba(255, 255, 255, 0.66);
	background: transparent;
	cursor: pointer;
	transition:
		color 160ms ease,
		background 160ms ease;

	&:hover {
		color: #fff;
		background: rgba(255, 255, 255, 0.06);
	}
`;
